// The portal's own URL space, and the website's around it (#160 AF10).
//
// CMS pages keep their TYPO3 paths at the site root (`/fsg/accommodation`), so
// the router ends in a catch-all that asks the cms service for whatever path
// nothing else claimed. The app's routes ALWAYS win: a path whose first
// segment belongs to the app (a tool, a public site page, the identity funnel)
// or to the serving shell (static files, the auth and graph endpoints) is
// never looked up as a CMS page, even if the CMS holds a page there — the
// editor cannot take `/deadlines/…` away from the deadlines tool.
//
// src/router/cmsCatchAll.invariant.test.ts derives the first segments from
// src/router/routes.ts and fails until this list matches them, so a new tool
// route cannot be added without reserving its prefix here.
//
// Kept in `src/core/` and free of imports: the auth boot reads it (through
// publicPaths.ts) before any module loads.

/** First path segments the app's own routes live under. */
export const APP_ROUTE_SEGMENTS = [
  'account-info',
  'announcements',
  'audit',
  'ceremony',
  'claim',
  'core',
  'deadlines',
  'docs',
  'documents',
  'event',
  'expenses',
  'finance',
  'hotel',
  'member-documents',
  'messages',
  'my',
  'newsroom',
  'press',
  'rbac',
  'recovery',
  'reports',
  'results',
  'rules',
  'scoring',
  'setup',
  'signin',
  'signup',
  'sponsors',
  'statics-feedback',
  'teams',
  'tickets',
  'today',
  'tvsd',
  'verify',
  'website',
] as const

/** Deeper prefixes the app owns inside a first segment the website keeps:
 *  `/pr/…` holds CMS pages (press contacts, the media kit), while
 *  `/pr/news/…` is the News module's public pages (#162 AH6 — the TYPO3
 *  addresses kept verbatim). Segment-exact, like the list above: `/pr/newsletter`
 *  stays a CMS candidate. `/tools/map/…` is the map editor (#169 WP8a); the
 *  rest of `/tools` stays the website's. */
export const APP_ROUTE_PREFIXES = ['pr/news', 'tools/map'] as const

/** Single paths the app owns while everything BELOW them stays the
 *  website's: `/partners` is the partners app page (#168 AP6), and
 *  `/partners/bosch` and the other partner pages are CMS pages. Exact, so
 *  `/partners/…` remains a CMS candidate. Likewise `/world/competitions`,
 *  World › Competitions (#170): the rest of `/world` (`/world/rankings-2019`
 *  and every other page of the section) stays the website's. */
export const APP_ROUTE_EXACT = ['partners', 'world/competitions'] as const

/** First path segments the serving shell answers itself (static assets, the
 *  token and graph endpoints, the silent-check page). The SPA never sees them
 *  on a real deployment; listing them keeps the catch-all from ever trying. */
export const SHELL_SEGMENTS = [
  'assets',
  'authnz',
  'brand',
  'fonts',
  'graphql',
  'icon',
  // The event map's service worker (#169 AO8, public/map-sw.js).
  'map-sw.js',
  // The announcements board's push worker (#164, public/today-sw.js).
  'today-sw.js',
  'silent-check-sso.html',
] as const

const RESERVED = new Set<string>([...APP_ROUTE_SEGMENTS, ...SHELL_SEGMENTS])

/** The first segment of a path (`/fsg/accommodation` → `fsg`, `/` → ''). */
export function firstSegment(path: string): string {
  return path.split(/[?#]/, 1)[0]!.split('/').filter(Boolean)[0] ?? ''
}

/** The path's segments, lower-cased, without query or hash. */
function segmentsOf(path: string): string[] {
  return path
    .split(/[?#]/, 1)[0]!
    .split('/')
    .filter(Boolean)
    .map((s) => s.toLowerCase())
}

/** The APP_ROUTE_PREFIXES entry `path` lies under, or null. */
export function reservedPrefixOf(path: string): string | null {
  const segs = segmentsOf(path)
  for (const p of APP_ROUTE_PREFIXES) {
    const want = p.split('/')
    if (want.every((w, i) => segs[i] === w)) return p
  }
  return null
}

/** The APP_ROUTE_EXACT entry `path` is (segment-exact, any case), or null. */
export function reservedExactOf(path: string): string | null {
  const joined = segmentsOf(path).join('/')
  return APP_ROUTE_EXACT.find((p) => p === joined) ?? null
}

/** Whether `path` belongs to the app or the shell rather than the CMS. The
 *  site root `/` is the public home page — reserved too. */
export function isReservedAppPath(path: string): boolean {
  const seg = firstSegment(path)
  return (
    seg === '' ||
    RESERVED.has(seg.toLowerCase()) ||
    reservedPrefixOf(path) !== null ||
    reservedExactOf(path) !== null
  )
}

/** Whether the catch-all may look `path` up as a CMS page. */
export function isCmsCandidatePath(path: string): boolean {
  return !isReservedAppPath(path)
}
